import { Button, Text } from '@loadsmart/loadsmart-ui'
import { Typography, Box, Stack, Tooltip, CircularProgress } from '@mui/material'
import { AutoFixHigh as MagicWand, AccountTree } from '@mui/icons-material'
import { useState, useCallback, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import X12CanonicalSpec from './X12CanonicalSpec'
import X12SchemaGenerationWizard from './X12SchemaGenerationWizard'
import { toast } from 'react-toastify'
import { safeBase64Encode, safeBase64Decode } from '../../../common/helpers/encoding'

export interface X12SchemaGeneratorProps {
  value?: string
  onChange: (changed: string) => void
  modelId?: string
}

export default function X12SchemaManager(props: X12SchemaGeneratorProps) {
  // Unified schema data state - always represents the decoded JSON structure
  const [schemaData, setSchemaData] = useState<any>(null)
  const [isAcceptingSpec, setIsAcceptingSpec] = useState<boolean>(false)

  // Initialize schema data from props.value (base64 decoded)
  useEffect(() => {
    try {
      if (props.value && props.value.trim() !== '') {
        // Decode base64 string to JSON
        const decodedString = safeBase64Decode(props.value)

        // Handle empty decoded string
        if (!decodedString || decodedString.trim() === '') {
          setSchemaData(null)
          return
        }

        const decodedData = JSON.parse(decodedString)

        // Validate that we have meaningful data
        if (decodedData && typeof decodedData === 'object') {
          setSchemaData(decodedData)
        } else {
          setSchemaData(null)
        }
      } else {
        setSchemaData(null)
      }
    } catch (error) {
      console.warn('Failed to parse structure data:', error)
      setSchemaData(null)
    }
  }, [props.value])

  // Wizard dialog state
  const [showWizardDialog, setShowWizardDialog] = useState<boolean>(false)

  const handleOpenWizard = useCallback(() => {
    setShowWizardDialog(true)
  }, [])

  const handleCloseWizard = useCallback(() => {
    setShowWizardDialog(false)
  }, [])

  const handleAcceptGeneratedSpec = useCallback(
    (acceptedSpec: any) => {
      setIsAcceptingSpec(true)

      // Defer expensive operations to prevent UI blocking
      setTimeout(() => {
        // Use requestAnimationFrame to defer heavy operations
        requestAnimationFrame(() => {
          // Defer JSON.stringify operation
          setTimeout(() => {
            setSchemaData(acceptedSpec)

            // Defer base64 encoding
            setTimeout(() => {
              const updatedStructure = JSON.stringify(acceptedSpec, null, 2)
              props.onChange(safeBase64Encode(updatedStructure))
              toast.success('Generated specification accepted and applied successfully!')
              setIsAcceptingSpec(false)
            }, 0)
          }, 0)
        })
      }, 0)
    },
    [props]
  )

  const handleFormChange = (data: any, errors?: any) => {
    // Update local state immediately for responsive UI
    setSchemaData(data)

    // Defer expensive operations to prevent UI blocking
    setTimeout(() => {
      // Use requestAnimationFrame to defer heavy operations
      requestAnimationFrame(() => {
        // Defer JSON.stringify operation
        setTimeout(() => {
          // Defer base64 encoding
          setTimeout(() => {
            const updatedStructure = JSON.stringify(data, null, 2)
            props.onChange(safeBase64Encode(updatedStructure))
          }, 0)
        }, 0)
      })
    }, 0)
  }

  return (
    <Box sx={{ height: '100%' }}>
      {/* Single Column - Generated Structure */}
      {/* <Paper elevation={1} sx={{ p: 1.5, height: '100%', overflow: 'hidden' }}> */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree sx={{ fontSize: 20, color: '#666' }} />
          <Typography variant="h6" fontWeight={'bold'}>
            Structure
          </Typography>
        </Box>
        <Tooltip title="Generate X12 schema from resources and customizations">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="primary" onClick={handleOpenWizard} leading={<MagicWand />}>
              Generate from Spec
            </Button>
          </Box>
        </Tooltip>
      </Box>
      <Box sx={{ mt: 1, height: 'calc(100% - 50px)', position: 'relative' }}>
        <Stack spacing={1} sx={{ height: '100%' }}>
          {schemaData ? (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <ErrorBoundary
                FallbackComponent={({ error, resetErrorBoundary }) => (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="error" gutterBottom>
                      Error rendering form: {error.message}
                    </Typography>
                    <Button variant="secondary" onClick={resetErrorBoundary}>
                      Retry
                    </Button>
                  </Box>
                )}
                onReset={() => {}}
              >
                <X12CanonicalSpec data={schemaData} onChange={handleFormChange} />
              </ErrorBoundary>
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                p: 4,
              }}
            >
              <Text variant="body" color="color-neutral">
                {props.value
                  ? 'Schema structure loaded from existing data'
                  : 'Generated schema structure will appear here after clicking Generate from Spec'}
              </Text>
            </Box>
          )}
        </Stack>

        {/* Loading overlay */}
        {isAcceptingSpec && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: '4px',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Applying generated specification...
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {/* </Paper> */}

      {/* Wizard Dialog */}
      <X12SchemaGenerationWizard
        open={showWizardDialog}
        onClose={handleCloseWizard}
        onAccept={handleAcceptGeneratedSpec}
        modelId={props.modelId}
      />
    </Box>
  )
}
